const DRAIN_POLL_MS = 100;

let anchorMs: number | null = null;
let speechEndMs = 0;
let pausedAtMs: number | null = null;
let drainTimer: ReturnType<typeof setInterval> | null = null;

const stopDrainWatch = (): void => {
  if (drainTimer === null) return;
  clearInterval(drainTimer);
  drainTimer = null;
};

export const beginSilentTurn = (): void => {
  stopDrainWatch();
  anchorMs = performance.now();
  speechEndMs = 0;
  pausedAtMs = null;
};

export const endSilentTurn = (): void => {
  stopDrainWatch();
  anchorMs = null;
  speechEndMs = 0;
  pausedAtMs = null;
};

export const isSilentTurn = (): boolean => anchorMs !== null;

/**
 * Freeze the clock — a pronunciation lookup or a canvas-close confirmation
 * suspends the PCM player and the caption queue the same way, but this clock
 * is a second, independent timeline that nothing else was stopping: the
 * tutor-action queue reads it with no pause ref of its own, and the DONE
 * drain watch polls it on a bare setInterval. Freezing here is what pausing
 * those from a single place actually means, rather than re-deriving "are we
 * paused" in every reader.
 */
export const pauseSilentTurn = (): void => {
  if (anchorMs === null || pausedAtMs !== null) return;
  pausedAtMs = performance.now();
};

export const resumeSilentTurn = (): void => {
  if (anchorMs === null || pausedAtMs === null) return;
  // Shift the anchor forward by the paused span so elapsed time picks up
  // exactly where it left off, rather than including the pause.
  anchorMs += performance.now() - pausedAtMs;
  pausedAtMs = null;
};

export const silentElapsedMs = (): number | null => {
  if (anchorMs === null) return null;
  return (pausedAtMs ?? performance.now()) - anchorMs;
};

const extendSpeech = (ms: number): void => {
  if (anchorMs !== null && ms > speechEndMs) speechEndMs = ms;
};

/**
 * A word's onset. Only a floor on when the turn can end — the word still has
 * to be spoken after it starts. Tutor actions carry an onset and no duration,
 * so this is all they can contribute.
 */
export const noteScheduled = (startMs: number): void => extendSpeech(startMs);

/**
 * The end of a spoken token: start_ms + duration_ms of its last word. This is
 * the real answer to "when does this turn stop talking", and the only one for
 * a token scheduled at a single onset — a rendered math token, or a one-word
 * reply — where the onset alone says nothing about how long it lasts.
 */
export const noteSpeechEnd = (endMs: number): void => extendSpeech(endMs);

export const silentSpeechRemainingMs = (): number | null => {
  const elapsed = silentElapsedMs();
  return elapsed === null ? null : speechEndMs - elapsed;
};

export const watchSilentTurnDrain = (onDrained: () => void): void => {
  if (anchorMs === null) return;
  stopDrainWatch();
  drainTimer = setInterval(() => {
    // Elapsed time is frozen while paused, and it can already be at or past
    // the known speech end the moment the modal opens — DONE can lag behind
    // the synthetic pacing enough that the turn reads as "already over"
    // before anyone paused it. Checking pausedAtMs directly, rather than
    // trusting a frozen remaining value, is what keeps that from draining
    // the turn behind the modal; resuming lets the next tick see it.
    if (pausedAtMs !== null) return;
    const remaining = silentSpeechRemainingMs();
    if (remaining === null) {
      stopDrainWatch();
      return;
    }
    if (remaining > 0) return;
    endSilentTurn();
    onDrained();
  }, DRAIN_POLL_MS);
};
